/**
 * On-demand agent review of a STORED counterparty version —
 * `POST /admin/data/dscr-pricing/{cpId}/agent-review?version=&store=`.
 *
 * Re-runs the grid-recognition agent against that version's original workbook
 * and the overlay agent against its pricing, so a sheet imported before the
 * agent existed gets the same review surface a new import gets.
 *
 * READ THIS AS A REVIEW LENS, NOT A SOURCE OF TRUTH — which is why the dialog
 * leads with the agreement table rather than the agent's own grid list. The
 * DETERMINISTIC parse is what was stored and what prices loans; the agent's
 * recognition varies slightly run to run (it sometimes merges the two LLPA
 * tables, so 7 grids one run and 8 the next). A disagreement is a prompt to go
 * look at the workbook, not evidence that the stored pricing is wrong.
 *
 * `store` persists the result as `lastAgentReview` on the counterparty. It does
 * NOT write a new version and never touches `pricing`.
 */
import { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import CheckCircleOutlined from "@mui/icons-material/CheckCircleOutlined";
import WarningAmberRounded from "@mui/icons-material/WarningAmberRounded";
import { useSnackbar } from "notistack";

import Flx from "../../../components/layout/Flx";
import Txt from "../../../components/typography/Txt";
import { adminDscrPricingApi } from "../../../api/adminDataApi";
import { A } from "../shared/adminTokens";
import { AdminEmpty } from "../shared/AdminScreenShell";
import { MetaChip, SectionEmpty, T } from "./pricingUi";

const GRID_LABELS = {
  llpa_table: "LLPA tables",
  coupon_ladder: "Coupon ladders",
  tier_margins: "Tier margins",
  min_max_prices: "Min / max prices",
};

const Section = ({ title, meta, children }) => (
  <Flx column g={1}>
    <Flx ac g={1}>
      <Txt sx={{ fontSize: 12.5, fontWeight: 600, color: T.ink }}>{title}</Txt>
      {meta ? <MetaChip>{meta}</MetaChip> : null}
    </Flx>
    {children}
  </Flx>
);

Section.propTypes = {
  title: PropTypes.node,
  meta: PropTypes.node,
  children: PropTypes.node,
};

export default function DscrAgentReviewDialog({ open, cpId, version, onClose }) {
  const { enqueueSnackbar } = useSnackbar();
  const [review, setReview] = useState(null);
  const [busy, setBusy] = useState(false);

  const run = useCallback(
    ({ store } = {}) => {
      if (!cpId) return;
      setBusy(true);
      adminDscrPricingApi
        .agentReview(cpId, { version, store })
        .then((data) => {
          setReview(data);
          if (store) {
            enqueueSnackbar("Review saved to the counterparty", {
              variant: "success",
            });
          }
        })
        .catch((err) =>
          enqueueSnackbar(
            err?.response?.data?.detail || "Agent review failed",
            { variant: "error" },
          ),
        )
        .finally(() => setBusy(false));
    },
    [cpId, version, enqueueSnackbar],
  );

  useEffect(() => {
    if (open) {
      setReview(null);
      run();
    }
  }, [open, run]);

  const warnings = review?.gridAgent?.validation?.warnings || [];
  const agreement = review?.gridAgent?.validation?.agreement || [];
  const overlays = review?.overlaySuggestions || [];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontSize: 16, fontWeight: 700 }}>
        Agent review — {cpId}
        {version != null ? ` · v${version}` : ""}
      </DialogTitle>
      <DialogContent dividers>
        {busy && !review ? (
          <Flx center sx={{ py: 6 }}>
            <CircularProgress size={22} />
          </Flx>
        ) : !review ? (
          <AdminEmpty>No review returned.</AdminEmpty>
        ) : (
          <Flx column g={2.5}>
            <Txt
              sx={{ fontSize: 12, color: T.muted, display: "block", lineHeight: 1.55 }}
            >
              The stored pricing comes from the deterministic parse — this is a
              second reading of the same workbook, for review only. Nothing here
              changes what prices loans.
            </Txt>

            {!review.sourceWorkbookAvailable ? (
              <Flx
                ac
                g={1}
                sx={{
                  p: 1.5,
                  borderRadius: "8px",
                  bgcolor: T.warnSoft,
                  border: `1px solid ${T.warnLine}`,
                }}
              >
                <WarningAmberRounded sx={{ fontSize: 18, color: T.warn }} />
                <Txt sx={{ fontSize: 12.5, color: T.warn }}>
                  This version has no original workbook stored, so only the
                  overlay agent ran.
                </Txt>
              </Flx>
            ) : null}

            <Section
              title="Warnings"
              meta={warnings.length ? `${warnings.length}` : undefined}
            >
              {warnings.length ? (
                <Flx column g={1}>
                  {warnings.map((w, i) => (
                    <Flx
                      key={i}
                      g={1}
                      sx={{
                        alignItems: "flex-start",
                        p: 1.25,
                        borderRadius: "8px",
                        bgcolor: T.warnSoft,
                        border: `1px solid ${T.warnLine}`,
                      }}
                    >
                      <WarningAmberRounded
                        sx={{ fontSize: 17, color: T.warn, flexShrink: 0 }}
                      />
                      <Txt sx={{ fontSize: 12.5, color: T.ink2, lineHeight: 1.5 }}>
                        {w}
                      </Txt>
                    </Flx>
                  ))}
                </Flx>
              ) : (
                <Flx ac g={1}>
                  <CheckCircleOutlined sx={{ fontSize: 17, color: A.ok }} />
                  <Txt sx={{ fontSize: 12.5, color: T.muted }}>
                    Nothing flagged.
                  </Txt>
                </Flx>
              )}
            </Section>

            <Section title="Parser vs agent">
              {agreement.length ? (
                <Box
                  sx={{
                    border: `1px solid ${T.line}`,
                    borderRadius: "8px",
                    overflow: "hidden",
                  }}
                >
                  {agreement.map((row, i) => {
                    const disagrees = row.parser !== row.agent;
                    return (
                      <Flx
                        ac
                        jb
                        key={row.gridType}
                        sx={{
                          px: 1.75,
                          py: 1,
                          borderTop: i ? `1px solid ${T.lineSoft}` : "none",
                          bgcolor: disagrees ? T.warnSoft : "transparent",
                        }}
                      >
                        <Txt sx={{ fontSize: 12.5, color: T.ink }}>
                          {GRID_LABELS[row.gridType] || row.gridType}
                        </Txt>
                        <Txt
                          sx={{
                            fontFamily: T.mono,
                            fontSize: 12,
                            color: disagrees ? T.warn : T.muted,
                          }}
                        >
                          parser {row.parser} · agent {row.agent}
                        </Txt>
                      </Flx>
                    );
                  })}
                </Box>
              ) : (
                <SectionEmpty>The agent didn&apos;t return a reading.</SectionEmpty>
              )}
            </Section>

            <Section
              title="Overlay suggestions"
              meta={overlays.length ? `${overlays.length}` : undefined}
            >
              {overlays.length ? (
                <Flx column g={0.75}>
                  {overlays.slice(0, 8).map((o, i) => (
                    <Txt
                      key={i}
                      sx={{ fontSize: 12.5, color: T.ink2, lineHeight: 1.5 }}
                    >
                      • {o.rule || JSON.stringify(o)}
                    </Txt>
                  ))}
                  {overlays.length > 8 ? (
                    <Txt sx={{ fontSize: 11.5, color: T.faint }}>
                      + {overlays.length - 8} more — they land in the Guideline
                      overlays section when you accept them.
                    </Txt>
                  ) : null}
                </Flx>
              ) : (
                <SectionEmpty>No overlays suggested.</SectionEmpty>
              )}
              {review.overlayAgentNotes ? (
                <Txt sx={{ fontSize: 12, color: T.muted, lineHeight: 1.5 }}>
                  {review.overlayAgentNotes}
                </Txt>
              ) : null}
            </Section>
          </Flx>
        )}
      </DialogContent>
      <DialogActions>
        <Button variant="text" onClick={onClose}>
          Close
        </Button>
        <Button variant="text" onClick={() => run()} disabled={busy}>
          Re-run
        </Button>
        {/* Saving keeps the review on the counterparty for the next person; it
            writes `lastAgentReview`, not a version. */}
        <Button
          variant="contained"
          onClick={() => run({ store: true })}
          disabled={busy || !review}
        >
          Save this review
        </Button>
      </DialogActions>
    </Dialog>
  );
}

DscrAgentReviewDialog.propTypes = {
  open: PropTypes.bool,
  cpId: PropTypes.string,
  version: PropTypes.number,
  onClose: PropTypes.func,
};
